import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  cartItems: localStorage.getItem("cartItems")
    ? JSON.parse(localStorage.getItem("cartItems"))
    : [],
  cartTotalQuantity: 0,
  cartTotalAmount: 0,
};

/** Helper: resolve the effective stock for a cart item */
const getStock = (item) => {
  if (item.selectedVariant?.stock_quantity != null) {
    return item.selectedVariant.stock_quantity;
  }
  return item.stock_quantity ?? Infinity;
};

export const CartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) =>
          item.id === action.payload.id &&
          item.selectedVariant?.id === action.payload.selectedVariant?.id
      );
      const stock = getStock(action.payload);

      if (productIndex >= 0) {
        // Already in cart — increment only if stock allows
        const currentQty = state.cartItems[productIndex].cartQuantity;
        if (currentQty < stock) {
          state.cartItems[productIndex].cartQuantity += 1;
        }
        // else: silently cap — caller shows the warning toast
      } else {
        const tempProduct = { ...action.payload, cartQuantity: 1 };
        state.cartItems.push(tempProduct);
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    removeFromCart(state, action) {
      state.cartItems = state.cartItems.filter(
        (item) =>
          !(
            item.id === action.payload.id &&
            item.selectedVariant?.id === action.payload.selectedVariant?.id
          )
      );
      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    decreaseCart(state, action) {
      const itemIndex = state.cartItems.findIndex(
        (item) =>
          item.id === action.payload.id &&
          item.selectedVariant?.id === action.payload.selectedVariant?.id
      );

      if (itemIndex >= 0 && state.cartItems[itemIndex].cartQuantity > 1) {
        state.cartItems[itemIndex].cartQuantity -= 1;
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    incrementCart(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) =>
          item.id === action.payload.id &&
          item.selectedVariant?.id === action.payload.selectedVariant?.id
      );
      const stock = getStock(action.payload);

      if (productIndex >= 0) {
        const currentQty = state.cartItems[productIndex].cartQuantity;
        if (currentQty < stock) {
          state.cartItems[productIndex].cartQuantity += 1;
        }
        // else: cap silently — UI button is disabled when atMax
      } else {
        const tempProduct = { ...action.payload, cartQuantity: 1 };
        state.cartItems.push(tempProduct);
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    addByIncrement(state, action) {
      const { product, cartQuantity } = action.payload;
      const productIndex = state.cartItems.findIndex(
        (item) =>
          item.id === product.id &&
          item.selectedVariant?.id === product.selectedVariant?.id
      );
      const stock = getStock(product);

      if (productIndex >= 0) {
        const currentQty = state.cartItems[productIndex].cartQuantity;
        const allowed = Math.min(cartQuantity, stock - currentQty);
        if (allowed > 0) {
          state.cartItems[productIndex].cartQuantity += allowed;
        }
      } else {
        const clampedQty = Math.min(cartQuantity, stock);
        const tempProduct = { ...product, cartQuantity: clampedQty };
        state.cartItems.push(tempProduct);
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    getTotals(state) {
      let { total, quantity } = state.cartItems.reduce(
        (cartTotal, cartItem) => {
          const { price, offer_price, cartQuantity, selectedVariant } = cartItem;

          const itemPrice = selectedVariant
            ? Number(selectedVariant.effective_price || 0)
            : Number(offer_price ? offer_price : price);

          const itemTotal = itemPrice * cartQuantity;

          cartTotal.total += itemTotal;
          cartTotal.quantity += cartQuantity;

          return cartTotal;
        },
        { total: 0, quantity: 0 }
      );
      total = parseFloat(total?.toFixed(2));
      state.cartTotalQuantity = quantity;
      state.cartTotalAmount = total;
    },

    clearCart(state) {
      state.cartItems = [];
      state.cartTotalQuantity = 0;
      state.cartTotalAmount = 0;
      localStorage.removeItem("cartItems");
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  decreaseCart,
  incrementCart,
  getTotals,
  addByIncrement,
  clearCart,
} = CartSlice.actions;

export const cartReducer = CartSlice.reducer;
